import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { CHECKLIST_STATUS_STYLES } from '../../constants';
import { getEffectiveChecklistStatus, isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ChecklistStatusDot from '../../reportCards/ChecklistStatusDot';
import EmptyTableRow from '../../reportCards/EmptyTableRow';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReadOnlyField from '../../reportCards/ReadOnlyField';
import ReportCard from '../../reportCards/ReportCard';
import ReportNote from '../../reportCards/ReportNote';
import SortTableCell from '../../reportCards/SortTableCell';
import { formatOptionalNumber, isFiniteNumber } from '../../shared/checklistUtils';
import type { ManualOverrideState } from '../../types';

import type { EgsCapacityChecklistRow } from './types';

interface Props {
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<EgsCapacityChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function CapacityReportCard({
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  return (
    <ReportCard>
      <ReportNote
        icon={<InfoOutlinedIcon />}
        text={
          <>
            <strong>EGS capacity check:</strong> auto-approve when{' '}
            <strong>Max Room Capacity = Included Capacity + EGS Capacity</strong>.
          </>
        }
      />
      <TableContainer>
        <Table size="small" sx={{ minWidth: 1180 }}>
          <TableHead>
            <TableRow>
              <SortTableCell>Deal Option / Room Asset</SortTableCell>
              <SortTableCell>Included Capacity</SortTableCell>
              <SortTableCell>Max Room Capacity</SortTableCell>
              <SortTableCell>EGS Contracted For</SortTableCell>
              <SortTableCell>Extra Guests</SortTableCell>
              <SortTableCell>Included + EGS</SortTableCell>
              <SortTableCell>Capacity Check</SortTableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={8} label="No EGS capacity rows match this view." />
            ) : (
              rows.map((row) => {
                const manuallyApproved = isRowManuallyApproved(row, manualOverrides);
                const approved = isRowApproved(row, manualOverrides);

                return (
                  <TableRow key={row.id} hover sx={approved ? CHECKLIST_STATUS_STYLES.approvedRow : undefined}>
                    <TableCell sx={{ minWidth: 300 }}>
                      <ReadOnlyField value={row.name} />
                      <ReadOnlyField subdued value={row.roomName} />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value={formatOptionalNumber(row.includedCapacity)} />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value={formatOptionalNumber(row.maxRoomCapacity)} />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value={formatOptionalNumber(row.egsCapacity)} />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value={formatOptionalNumber(row.egsCapacity)} />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField
                        value={
                          isFiniteNumber(row.includedCapacity) && isFiniteNumber(row.egsCapacity)
                            ? String(row.includedCapacity + row.egsCapacity)
                            : 'N/A'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <ChecklistStatusDot
                        approvalType={manuallyApproved ? 'manual' : 'auto'}
                        status={getEffectiveChecklistStatus(row, manualOverrides)}
                        band={approved ? 'green' : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      {approved ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<KeyboardReturnIcon />}
                          onClick={() => onUnapprovalChange(row.id)}
                        >
                          Unapprove
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => onApprovalChange(row.id)}
                        >
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="EGS capacity row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}
